import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    useTheme
} from '@mui/material';
import {
    Search as SearchIcon,
    PersonAdd as PersonAddIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import AdminLayout from './components/AdminLayout';
import EnrollmentTable from './components/enrollments/EnrollmentTable';
import {
    EnrollmentWithUser,
    getAllEnrollments,
    getEnrollmentsBySession,
    deleteEnrollment,
    updateEnrollmentPaymentStatus
} from '../../services/enrollmentService';
import { Session, getAllSessions } from '../../services/sessionService';

const NewEnrollmentManagementPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Data
    const [enrollments, setEnrollments] = useState<EnrollmentWithUser[]>([]);
    const [sessions, setSessions] = useState<Session[]>([]);
    const [totalEnrollments, setTotalEnrollments] = useState(0);

    // Filters & Pagination
    const [selectedSessionId, setSelectedSessionId] = useState<number | 'all'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const data = await getAllSessions();
            setSessions(data);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setError('Failed to load sessions.');
        }
    };

    const fetchEnrollments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let data;
            if (selectedSessionId === 'all') {
                data = await getAllEnrollments(page + 1, rowsPerPage);
            } else {
                data = await getEnrollmentsBySession(selectedSessionId, page + 1, rowsPerPage);
            }

            setEnrollments(data.enrollments);
            setTotalEnrollments(data.pagination.total);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching enrollments:', err);
            setError('Failed to load enrollments.');
            setLoading(false);
        }
    }, [page, rowsPerPage, selectedSessionId]);

    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);

    const handleDeleteEnrollment = async (enrollmentId: number) => {
        if (!window.confirm('Are you sure you want to remove this enrollment?')) return;

        try {
            await deleteEnrollment(enrollmentId);
            setSuccess('Enrollment removed successfully.');
            fetchEnrollments();
        } catch (err) {
            console.error('Error deleting enrollment:', err);
            setError('Failed to delete enrollment.');
        }
    };

    const handleUpdatePaymentStatus = async (enrollmentId: number, status: 'unpaid' | 'pending' | 'completed' | 'failed' | 'refunded') => {
        try {
            await updateEnrollmentPaymentStatus(enrollmentId, status);
            setSuccess(`Payment status updated to ${status}.`);
            fetchEnrollments();
        } catch (err) {
            console.error('Error updating payment status:', err);
            setError('Failed to update payment status.');
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        // Note: Backend search implementation would be ideal here.
        // For now, client-side filtering could be done if dataset is small, 
        // but with pagination, we really need backend search.
        // We'll implement client-side filtering on the current page for now as a fallback
        // or just rely on the backend if it supported it (it currently doesn't seem to support search query).
    };

    // Filter enrollments client-side for search (limited to current page)
    // Ideally, the API should support a search query param.
    const filteredEnrollments = enrollments.filter(enrollment => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            enrollment.user.firstName?.toLowerCase().includes(query) ||
            enrollment.user.lastName?.toLowerCase().includes(query) ||
            enrollment.user.email?.toLowerCase().includes(query)
        );
    });

    return (
        <AdminLayout>
            <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            Enrollment Management
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage student enrollments across all sessions.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        sx={{ borderRadius: 2, px: 3, py: 1 }}
                    // onClick={() => setOpenAddDialog(true)} // TODO: Implement Add Dialog
                    >
                        Add Enrollment
                    </Button>
                </Box>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}

                {/* Filters & Actions */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center'
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by Session</InputLabel>
                        <Select
                            value={selectedSessionId}
                            label="Filter by Session"
                            onChange={(e) => {
                                setSelectedSessionId(e.target.value as number | 'all');
                                setPage(0); // Reset to first page on filter change
                            }}
                        >
                            <MenuItem value="all">All Sessions</MenuItem>
                            {sessions.map((session) => (
                                <MenuItem key={session.id} value={session.id}>
                                    {session.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        placeholder="Search students..."
                        size="small"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                    />

                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={fetchEnrollments}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FilterIcon />}
                        >
                            More Filters
                        </Button>
                    </Box>
                </Paper>

                {/* Enrollment Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <EnrollmentTable
                        enrollments={filteredEnrollments}
                        total={totalEnrollments}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        onDeleteEnrollment={handleDeleteEnrollment}
                        onUpdatePaymentStatus={handleUpdatePaymentStatus}
                    />
                )}
            </Box>
        </AdminLayout>
    );
};

export default NewEnrollmentManagementPage;
